/**
 * Rueckmeldung zur Analyse, unten rechts auf der Dokumentseite.
 *
 * Im Pilot pruefen die Berufstraeger die Analysen. Statt Notizen zu sammeln
 * und zu schicken, schreiben oder sprechen sie hier hinein, was passt, was
 * ueberfluessig ist und was fehlt. Welche Analyse und welcher Mandant, weiss
 * der Kasten selbst.
 *
 * Gesprochenes wandelt der eigene Server mit Whisper in Text um; der Text
 * landet im Feld und kann vor dem Senden korrigiert werden. Die Aufnahme
 * selbst wird nirgends gespeichert.
 *
 * Der Kasten bleibt beim Schliessen erhalten: ein angefangener Text ist nach
 * dem Wiederoeffnen noch da.
 */
import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'

interface Eigene {
  id: string
  text: string
  erstellt_am: string
}

/** Laenger spricht niemand am Stueck; die Umwandlung dauert etwa so lange wie die Aufnahme. */
const HOECHSTENS_SEKUNDEN = 5 * 60

const FORMATE = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus']

function aufnahmeMoeglich(): boolean {
  return typeof window !== 'undefined' && window.isSecureContext && Boolean(navigator.mediaDevices?.getUserMedia) && typeof MediaRecorder !== 'undefined'
}

function uhr(sekunden: number): string {
  return `${Math.floor(sekunden / 60)}:${String(sekunden % 60).padStart(2, '0')}`
}

function zeitpunkt(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

export function Rueckmeldekasten({ dokumentId }: { dokumentId: string }) {
  const kunde = useQueryClient()
  const [offen, setOffen] = useState(false)
  const [text, setText] = useState('')
  const [gesprochen, setGesprochen] = useState(false)
  const [aufnahme, setAufnahme] = useState<'aus' | 'startet' | 'laeuft' | 'wandelt'>('aus')
  const [sekunden, setSekunden] = useState(0)
  const [fehler, setFehler] = useState<string | null>(null)
  const [gesendet, setGesendet] = useState(false)

  const rekorder = useRef<MediaRecorder | null>(null)
  const feld = useRef<HTMLTextAreaElement>(null)
  /* Steht die Seite noch? Nach dem Verlassen darf weder eine Aufnahme
     beginnen (die Freigabe kann spaeter kommen) noch eine begonnene noch
     hochgeladen werden. */
  const aktiv = useRef(true)

  const eigene = useQuery({
    queryKey: ['rueckmeldungen', 'eigene', dokumentId],
    queryFn: () => api.holen<{ rueckmeldungen: Eigene[] }>(`/api/rueckmeldungen/eigene?dokument=${dokumentId}`),
    enabled: offen,
  })

  const senden = useMutation({
    mutationFn: () => api.senden('/api/rueckmeldungen', { dokumentId, text, gesprochen }),
    onSuccess: () => {
      setText('')
      setGesprochen(false)
      setGesendet(true)
      void kunde.invalidateQueries({ queryKey: ['rueckmeldungen', 'eigene', dokumentId] })
    },
    onError: (f) => setFehler(f instanceof Error ? f.message : 'Die Rückmeldung konnte nicht gesendet werden.'),
  })

  // Mikrofon freigeben, wenn die Seite verlassen wird. Die Aufnahme wird verworfen.
  useEffect(() => {
    aktiv.current = true
    return () => {
      aktiv.current = false
      const r = rekorder.current
      rekorder.current = null
      if (r) {
        r.onstop = null
        if (r.state === 'recording') r.stop()
        r.stream.getTracks().forEach((t) => t.stop())
      }
    }
  }, [])

  const aufnahmeBeenden = () => {
    if (rekorder.current?.state === 'recording') rekorder.current.stop()
  }

  // Die Uhr der laufenden Aufnahme; bei der Hoechstdauer endet sie von selbst.
  useEffect(() => {
    if (aufnahme !== 'laeuft') return
    const takt = setInterval(() => setSekunden((s) => s + 1), 1000)
    return () => clearInterval(takt)
  }, [aufnahme])

  useEffect(() => {
    if (sekunden >= HOECHSTENS_SEKUNDEN) aufnahmeBeenden()
  }, [sekunden])

  useEffect(() => {
    if (offen) feld.current?.focus()
  }, [offen])

  /** Laedt die Aufnahme hoch und fragt nach, bis Whisper fertig ist. */
  async function umwandeln(blob: Blob): Promise<string> {
    const { auftrag } = await api.senden<{ auftrag: string }>('/api/rueckmeldungen/abschrift', blob)
    for (;;) {
      await new Promise((weiter) => setTimeout(weiter, 1500))
      if (!aktiv.current) return ''
      const stand = await api.holen<{ stand: 'laeuft' } | { stand: 'fertig'; text: string }>(`/api/rueckmeldungen/abschrift/${auftrag}`)
      if (stand.stand === 'fertig') return stand.text
    }
  }

  async function aufnehmen() {
    if (aufnahme !== 'aus') return
    setFehler(null)
    setGesendet(false)
    setAufnahme('startet')
    let strom: MediaStream
    try {
      strom = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch {
      setFehler('Kein Zugriff auf das Mikrofon. Bitte im Browser für diese Seite erlauben.')
      setAufnahme('aus')
      return
    }
    if (!aktiv.current) {
      strom.getTracks().forEach((t) => t.stop())
      return
    }
    const format = FORMATE.find((f) => MediaRecorder.isTypeSupported(f))
    const teile: Blob[] = []
    let r: MediaRecorder
    try {
      // Fuer Sprache reichen 32 kbit/s; das drittelt den Upload gegenueber der Voreinstellung.
      r = new MediaRecorder(strom, { audioBitsPerSecond: 32_000, ...(format ? { mimeType: format } : {}) })
      r.ondataavailable = (e) => {
        if (e.data.size > 0) teile.push(e.data)
      }
      r.onstop = () => {
        strom.getTracks().forEach((t) => t.stop())
        rekorder.current = null
        const blob = new Blob(teile, { type: r.mimeType || format || 'audio/webm' })
        if (blob.size === 0) {
          setAufnahme('aus')
          return
        }
        setAufnahme('wandelt')
        umwandeln(blob)
          .then((erkannt) => {
            if (!aktiv.current) return
            if (!erkannt.trim()) {
              setFehler('In der Aufnahme war nichts zu verstehen.')
              return
            }
            setText((t) => (t.trim() ? `${t.trimEnd()}\n\n${erkannt}` : erkannt))
            setGesprochen(true)
          })
          .catch((f: unknown) => {
            if (aktiv.current) setFehler(f instanceof Error ? f.message : 'Die Aufnahme konnte nicht umgewandelt werden.')
          })
          .finally(() => {
            if (aktiv.current) setAufnahme('aus')
          })
      }
      r.start()
    } catch {
      strom.getTracks().forEach((t) => t.stop())
      setFehler('Die Aufnahme ließ sich in diesem Browser nicht starten. Bitte tippen.')
      setAufnahme('aus')
      return
    }
    rekorder.current = r
    setSekunden(0)
    setAufnahme('laeuft')
  }

  const liste = eigene.data?.rueckmeldungen ?? []

  return (
    <>
      {offen && (
        <section className="rueckmeldekasten" aria-label="Rückmeldung zur Analyse">
          <div className="rueckmeldekasten-kopf">
            <h2>Rückmeldung zur Analyse</h2>
            <button
              type="button"
              className="fenster-zu"
              onClick={() => {
                aufnahmeBeenden()
                setOffen(false)
              }}
              aria-label="Schließen"
            >
              ×
            </button>
          </div>

          <div className="rueckmeldekasten-inhalt">
            <p className="leiser">
              Was passt, was ist überflüssig, was fehlt? Schreiben oder einsprechen, gern mit dem Punkt, um den es geht. Die
              Rückmeldung geht direkt an H&amp;K.
            </p>

            <textarea
              ref={feld}
              rows={7}
              value={text}
              onChange={(e) => {
                setText(e.target.value)
                setGesendet(false)
              }}
              placeholder="Zum Beispiel: Punkt 3 ist überflüssig, das ist bei diesem Mandanten saisonal normal."
              disabled={aufnahme === 'wandelt'}
              aria-label="Rückmeldung"
              maxLength={20_000}
            />

            {fehler && <div className="fehler">{fehler}</div>}
            {gesendet && <div className="bestaetigung">Gesendet. Danke!</div>}

            <div className="rueckmeldekasten-fuss">
              {aufnahmeMoeglich() &&
                (aufnahme === 'laeuft' ? (
                  <button type="button" className="knopf aufnahme-laeuft" onClick={aufnahmeBeenden}>
                    <span className="aufnahme-punkt" aria-hidden="true" />
                    Stopp <span className="zahl">{uhr(sekunden)}</span>
                  </button>
                ) : (
                  <button type="button" className="knopf" onClick={() => void aufnehmen()} disabled={aufnahme !== 'aus' || senden.isPending}>
                    {aufnahme === 'wandelt' ? 'Wird in Text umgewandelt …' : aufnahme === 'startet' ? 'Mikrofon wird geöffnet …' : 'Einsprechen'}
                  </button>
                ))}
              <button
                type="button"
                className="knopf gefuellt"
                onClick={() => {
                  setFehler(null)
                  senden.mutate()
                }}
                disabled={!text.trim() || aufnahme !== 'aus' || senden.isPending}
              >
                {senden.isPending ? 'Wird gesendet …' : 'Senden'}
              </button>
            </div>

            {liste.length > 0 && (
              <div className="rueckmeldekasten-bisher">
                <div className="listenkopf">Bisher gesendet</div>
                {liste.map((r) => (
                  <div key={r.id} className="rueckmeldekasten-eintrag">
                    <div className="nebensache zahl">{zeitpunkt(r.erstellt_am)}</div>
                    <p>{r.text}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {!offen && (
        <button type="button" className="knopf rueckmeldeknopf" onClick={() => setOffen(true)}>
          Rückmeldung
          {text.trim() && <span className="leiser"> · Entwurf</span>}
        </button>
      )}
    </>
  )
}
